/**
 * A Veeva user-change proposal (ADR-0038): who, which fields (current →
 * requested), and what happens next. Nothing has been written to Veeva while
 * it is `proposed` — the requester's **Confirm** applies an ordinary change,
 * or submits a sensitive one for Administrator approval (ticket DQ-…). While
 * it awaits approval the card polls, so the requester sees the decision live.
 */

import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Group, Paper, Stack, Table, Text } from "@mantine/core";
import { IconCheck, IconShieldLock, IconX } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  cancelChange,
  confirmChange,
  fieldLabel,
  FINAL_STATUSES,
  getChange,
  type ChangeOut,
  type ChangeStatus,
} from "@/shared/api/userAdmin";
import { toastApiError } from "@/shared/ui/toast";
import { fmtValue } from "./picks";

const POLL_MS = 5000;

const STATUS_COLOR: Record<ChangeStatus, string> = {
  proposed: "blue",
  confirmed: "blue",
  applied: "green",
  failed: "red",
  cancelled: "gray",
  awaiting_approval: "orange",
  approved: "teal",
  rejected: "red",
  expired: "gray",
};

const STATUS_LABEL: Record<ChangeStatus, string> = {
  proposed: "Needs your confirmation",
  confirmed: "Applying",
  applied: "Applied in Veeva",
  failed: "Failed",
  cancelled: "Cancelled",
  awaiting_approval: "Awaiting admin approval",
  approved: "Approved",
  rejected: "Rejected",
  expired: "Expired",
};

export function ChangeStatusBadge({ status }: { status: ChangeStatus }) {
  return (
    <Badge color={STATUS_COLOR[status]} variant="light" data-testid="change-status">
      {STATUS_LABEL[status]}
    </Badge>
  );
}

export function ChangeTable({ change }: { change: ChangeOut }) {
  const sensitive = new Set(change.sensitive_fields);
  return (
    <Table withTableBorder fz="sm" data-testid="change-table">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Field</Table.Th>
          <Table.Th>Current</Table.Th>
          <Table.Th>Requested</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {change.changes.map((c) => (
          <Table.Tr key={c.field}>
            <Table.Td>
              <Group gap={6} wrap="nowrap">
                <Text size="sm">{fieldLabel(c.field)}</Text>
                {sensitive.has(c.field) && <IconShieldLock size={14} aria-label="needs approval" />}
              </Group>
            </Table.Td>
            <Table.Td c="dimmed">{fmtValue(c.old)}</Table.Td>
            <Table.Td fw={600}>{fmtValue(c.new)}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

export function ProposalCard({ initial, changeId }: { initial?: ChangeOut; changeId: string }) {
  const getToken = useAccessToken();
  const [change, setChange] = useState<ChangeOut | null>(initial ?? null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setChange(await getChange(await getToken(), changeId));
    } catch {
      /* transient — the next poll retries */
    }
  }, [changeId, getToken]);

  useEffect(() => {
    if (!change) void refresh();
  }, [change, refresh]);

  const status = change?.status;
  useEffect(() => {
    if (status !== "awaiting_approval" && status !== "confirmed") return;
    const t = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(t);
  }, [status, refresh]);

  if (!change) {
    return (
      <Paper withBorder p="sm" radius="md">
        <Text size="sm" c="dimmed">
          Loading proposal…
        </Text>
      </Paper>
    );
  }

  const act = async (fn: typeof confirmChange, label: string) => {
    setBusy(true);
    try {
      setChange(await fn(await getToken(), change.change_id));
    } catch (err) {
      toastApiError(label, err);
      void refresh();
    } finally {
      setBusy(false);
    }
  };

  const open = change.status === "proposed" || change.status === "failed";
  const title =
    change.kind === "create"
      ? `Create Veeva user ${change.target_label}`
      : `Update Veeva ${change.target_label}`;
  return (
    <Paper withBorder p="sm" radius="md" data-testid="proposal-card">
      <Stack gap="xs">
        <Group justify="space-between" wrap="wrap" gap="xs">
          <Text fw={600} size="sm">
            {title}
          </Text>
          <Group gap={6}>
            {change.needs_approval && (
              <Badge color="orange" variant="outline" leftSection={<IconShieldLock size={12} />}>
                Needs admin approval
              </Badge>
            )}
            <ChangeStatusBadge status={change.status} />
          </Group>
        </Group>
        <ChangeTable change={change} />
        {change.status === "awaiting_approval" && (
          <Text size="xs" c="dimmed">
            Sent for admin approval{change.ticket_number ? ` — ticket ${change.ticket_number}` : ""}
            . Expires {new Date(change.expires_at_utc).toLocaleDateString()}.
          </Text>
        )}
        {change.result_message && change.status !== "applied" && (
          <Text size="xs" c="red">
            {change.result_message}
          </Text>
        )}
        {change.decision_reason && (
          <Text size="xs" c="dimmed">
            Administrator's reason: {change.decision_reason}
          </Text>
        )}
        {open && (
          <Group gap="xs">
            <Button
              size="xs"
              leftSection={<IconCheck size={14} />}
              loading={busy}
              onClick={() => void act(confirmChange, "Could not confirm the change")}
              data-testid="proposal-confirm"
            >
              {change.needs_approval ? "Confirm and send for approval" : "Confirm"}
            </Button>
            <Button
              size="xs"
              variant="default"
              leftSection={<IconX size={14} />}
              disabled={busy}
              onClick={() => void act(cancelChange, "Could not cancel the change")}
            >
              Cancel
            </Button>
          </Group>
        )}
        {change.status === "awaiting_approval" && (
          <Group>
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              disabled={busy}
              onClick={() => void act(cancelChange, "Could not cancel the change")}
            >
              Withdraw request
            </Button>
          </Group>
        )}
        {open && (
          <Text size="xs" c="dimmed">
            Nothing has changed in Veeva yet.
          </Text>
        )}
        {FINAL_STATUSES.includes(change.status) && change.status === "applied" && (
          <Text size="xs" c="dimmed">
            Applied {change.applied_at_utc ? new Date(change.applied_at_utc).toLocaleString() : ""}
            {change.target_veeva_user_id ? ` · Veeva user ${change.target_veeva_user_id}` : ""}
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
